import type { DerivedSurface, LookScheme } from "@/types/look-system";
import { isColour, isName, record } from "@/utils/colour-values";
import { ownColourDarkVars, paletteSchemes } from "@/utils/colour-versions";
import {
  buttonTypeVars,
  chosenGlobalCss,
  givenWayRoleVars,
  namedGlobalVars,
  NO_COLOURS,
  type SchemeColours,
} from "@/utils/global-colours";
import { isLookSystem } from "@/utils/look-gate";
import { lookSchemeSettings, resolveLookScheme } from "@/utils/look-scheme";
import { ROLE_PALETTE_KEYS, rolePaletteKey } from "@/utils/palette-roles";
import { legacyVarsFromSurface } from "@/utils/website-appearance";

type Vars = Record<string, string>;

const SCOPE = "html[data-re-colors]";
// Not html[data-re-scheme=…][data-re-colors]: guide/preview.tsx rewrites html[data-re-scheme="…"] to :root.
const DARK_SCOPE = `${SCOPE}[data-re-scheme="dark"]`;
// An own colour's data-re-bg is its hex, which no palette key can be.
const OWN = '[data-re-bg^="#"]';
const LEGACY_VAR = /^--[a-z0-9-]{1,80}$/;
const LEGACY_VALUE = /^[a-z0-9#(),.% /-]{1,200}$/i;
const LEGACY_FUNCTIONS = new Set([
  "rgb",
  "rgba",
  "hsl",
  "hsla",
  "hwb",
  "lab",
  "lch",
  "oklab",
  "oklch",
  "color",
  "color-mix",
  "var",
]);

export function surfaceOf(value: unknown): DerivedSurface | undefined {
  const surface = value as Partial<DerivedSurface> | null | undefined;
  const btn = surface?.btn;
  const colours = [
    surface?.bg,
    surface?.card,
    surface?.text,
    surface?.heading,
    surface?.muted,
    surface?.border,
    surface?.control,
    surface?.link,
    surface?.eyebrow,
    surface?.focus,
    surface?.error,
    surface?.success,
    btn?.bg,
    btn?.fg,
    btn?.border,
    btn?.hover?.bg,
    btn?.hover?.fg,
    btn?.active?.bg,
    btn?.active?.fg,
  ];
  return colours.every(isColour) ? (surface as DerivedSurface) : undefined;
}

/**
 * What --button-bg-color holds on the page in `scheme`, by default the one it is drawn in before a visitor chooses (its
 * base, or the scheme Lys og mørk fixes or starts in): a stored legacy override where that scheme is the base (the
 * legacy rule is the base scheme's and comes last), else that scheme's page button; the dark rule inherits the light
 * one's without a dark page, and Design's button colour holds it without a page at all.
 */
export function pageButtonColour(
  theme: unknown,
  drawn?: LookScheme,
): string | undefined {
  const colorSystem = record(record(theme).colorSystem);
  const derived = record(colorSystem.derived);
  const settings = lookSchemeSettings(theme);
  const base: LookScheme = colorSystem.base === "dark" ? "dark" : "light";
  const scheme = drawn ?? (settings && resolveLookScheme(settings)) ?? base;
  const legacy = record(record(colorSystem.legacy)["--button-bg-color"]).hex;
  const pageButton = (of: LookScheme) =>
    surfaceOf(record(derived[of]).page)?.btn.bg;
  if (scheme === base && isColour(legacy)) return legacy;
  const design = record(record(theme).button).bgColor;
  return (
    (scheme === "dark" ? pageButton("dark") : undefined) ??
    (scheme === "dark" && isColour(legacy) ? legacy : undefined) ??
    pageButton("light") ??
    (typeof design === "string" ? design : undefined)
  );
}

export function surfaceVars(surface: DerivedSurface): Vars {
  return {
    ...legacyVarsFromSurface(surface),
    "re-card": surface.card,
    "re-heading": surface.heading,
    "re-link": surface.link,
    "re-eyebrow": surface.eyebrow,
    "re-control": surface.control,
    "re-focus": surface.focus,
    "re-success": surface.success,
    "re-button-bg": surface.btn.bg,
    "re-button-text": surface.btn.fg,
    "re-button-border": surface.btn.border,
    "re-button-hover-bg": surface.btn.hover.bg,
    "re-button-hover-text": surface.btn.hover.fg,
    "re-button-active-bg": surface.btn.active.bg,
    "re-button-active-text": surface.btn.active.fg,
  };
}

const schemeOf = (surface: DerivedSurface) =>
  surface.dark === true ? "dark" : "light";

function prefixed(prefix: string, vars: Vars): Vars {
  return Object.fromEntries(
    Object.entries(vars).map(([name, value]) => [`${prefix}${name}`, value]),
  );
}

function schemeColours(theme: unknown, scheme: LookScheme): SchemeColours {
  const colorSystem = record(record(theme).colorSystem);
  const derived = record(colorSystem.derived);
  const surfaces: Record<string, DerivedSurface> = {};
  for (const [key, value] of Object.entries(record(derived[scheme]))) {
    const surface = surfaceOf(value);
    if (isName(key) && surface) surfaces[key] = surface;
  }
  const palette: SchemeColours["palette"] = {};
  for (const entry of Array.isArray(colorSystem.palette)
    ? colorSystem.palette
    : []) {
    const held = paletteSchemes(colorSystem, entry);
    if (!held) continue;
    const { colour, own } = held[scheme];
    const version = own ? undefined : surfaces[held.key]?.bg;
    palette[held.key] = version
      ? { css: version, hex: version, held: colour }
      : { css: `var(--re-p-${held.key})`, hex: colour };
  }
  const high = record(colorSystem.options).contrast === "high";
  return {
    base: scheme === (colorSystem.base === "dark" ? "dark" : "light"),
    palette,
    redirects: record(colorSystem.redirects),
    globals: record(colorSystem.globals),
    buttons: record(colorSystem.buttons),
    surfaces,
    roles: record(derived.roles),
    levels: high ? { text: 7, nonText: 4.5 } : { text: 4.5, nonText: 3 },
  };
}

const lookVars = (
  surface: DerivedSurface,
  colours: SchemeColours,
  isPage: boolean,
): Vars => ({
  ...surfaceVars(surface),
  ...buttonTypeVars(colours, surface, isPage),
});

/** `paint` is the colour itself: the engine may have shifted the derived fill to reach the contrast level. */
function ownVars(
  paint: string,
  surface: DerivedSurface,
  colours: SchemeColours,
): Vars {
  return { ...lookVars(surface, colours, false), "re-own-bg": paint };
}

/**
 * The inline style of a section on an own background colour. The dark scheme's colours wait as --re-dark-*, which
 * colorSystemCss's dark rule puts in place.
 */
export function ownBackgroundStyle(
  hex: string,
  light: DerivedSurface,
  dark: DerivedSurface,
  theme?: unknown,
): Vars {
  return {
    colorScheme: schemeOf(light),
    ...prefixed("--", ownVars(hex, light, schemeColours(theme, "light"))),
    "--re-dark-color-scheme": schemeOf(dark),
    ...prefixed(
      "--re-dark-",
      ownVars(dark.bg, dark, schemeColours(theme, "dark")),
    ),
  };
}

function uniformSurface(value: string): DerivedSurface {
  const pair = { bg: value, fg: value };
  return {
    bg: value,
    card: value,
    text: value,
    heading: value,
    muted: value,
    border: value,
    control: value,
    link: value,
    eyebrow: value,
    btn: { ...pair, border: value, hover: pair, active: pair },
    focus: value,
    error: value,
    success: value,
    dark: false,
  };
}

// Every surface yields the same variable names, so any surface lists them. Inline style yields only to !important.
const OWN_DARK_RULE = `${DARK_SCOPE} ${OWN} { ${[
  "color-scheme: var(--re-dark-color-scheme) !important;",
  ...Object.keys(ownVars("", uniformSurface(""), NO_COLOURS)).map(
    (name) => `--${name}: var(--re-dark-${name}) !important;`,
  ),
].join(" ")} }`;

// Text inherits the colour computed above the section, so redeclaring the variables alone would not reach it.
const SECTION_TEXT_RULE = `${SCOPE} [data-re-bg] { color: var(--re-text); }`;

function paletteVars(colorSystem: unknown, scheme: LookScheme): Vars {
  const vars: Vars = {};
  const palette = record(colorSystem).palette;
  for (const entry of Array.isArray(palette) ? palette : []) {
    const held = paletteSchemes(colorSystem, entry);
    const value = scheme === "dark" ? held?.darkDeclared : held?.light.colour;
    if (held && value) vars[`re-p-${held.key}`] = value;
  }
  return vars;
}

function redirectVars(redirects: unknown, palette: Vars): Vars {
  const vars: Vars = {};
  for (const [from, to] of Object.entries(record(redirects))) {
    if (isName(from) && isName(to) && !(`re-p-${from}` in palette)) {
      vars[`re-p-${from}`] = `var(--re-p-${to})`;
    }
  }
  return vars;
}

/** A role's key that names no colour on this website stands for the colour with that role, as templates name roles by key. */
function roleVars(colorSystem: unknown, taken: Vars): Vars {
  const vars: Vars = {};
  for (const key of Object.values(ROLE_PALETTE_KEYS)) {
    const target = rolePaletteKey(colorSystem, key);
    if (target && !(`re-p-${key}` in taken))
      vars[`re-p-${key}`] = `var(--re-p-${target})`;
  }
  return vars;
}

/** Only roles the derived colours do not already set: the CMS folds the globals it knows into `derived`. */
function globalVars(colours: SchemeColours, taken: Vars): Vars {
  const vars: Vars = {};
  for (const [role, value] of Object.entries(colours.globals)) {
    const name = `re-${role}`;
    if (!isName(role) || role.startsWith("p-") || name in taken) continue;
    const { ref, hex } = record(value);
    if (isName(ref)) vars[name] = chosenGlobalCss(colours, role, ref, taken);
    else if (isColour(hex)) vars[name] = hex;
  }
  return { ...vars, ...givenWayRoleVars(colours, taken) };
}

/** What `vars` sets otherwise than `inherited`, the light page rule's, which the dark one inherits. */
const changedFrom = (vars: Vars, inherited: Vars): Vars =>
  Object.fromEntries(
    Object.entries(vars).filter(([name, value]) => inherited[name] !== value),
  );

// Conversion keeps an old website's exact value here, including what was never valid hex ('undefined30',
// 'ffffff', var(--deleted)); only syntax that could load a resource or leave the declaration is refused.
function isLegacyValue(value: unknown): value is string {
  return (
    typeof value === "string" &&
    LEGACY_VALUE.test(value) &&
    [...value.matchAll(/([a-z-]*)\(/gi)].every(([, name]) =>
      LEGACY_FUNCTIONS.has(name.toLowerCase()),
    )
  );
}

function legacyVars(legacy: unknown): Vars {
  const vars: Vars = {};
  for (const [name, value] of Object.entries(record(legacy))) {
    const { hex } = record(value);
    if (LEGACY_VAR.test(name) && isLegacyValue(hex)) vars[name.slice(2)] = hex;
  }
  return vars;
}

function rule(selector: string, vars: Vars, surface?: DerivedSurface) {
  const declarations = Object.entries(vars).map(
    ([name, value]) => `--${name}: ${value};`,
  );
  if (surface) declarations.unshift(`color-scheme: ${schemeOf(surface)};`);
  return declarations.length ? `${selector} { ${declarations.join(" ")} }` : "";
}

function backgroundRules(scope: string, colours: SchemeColours) {
  return Object.entries(colours.surfaces).map(([key, surface]) =>
    rule(
      `${scope} [data-re-bg="${key}"]`,
      lookVars(surface, colours, key === "page"),
      surface,
    ),
  );
}

/**
 * The look system's colours as CSS, or '' for a website that is not on it. `derived` is the CMS's computed
 * output and is emitted as stored; `legacy` is the base scheme's exact per-variable overrides, emitted last.
 */
export function colorSystemCss(theme: unknown): string {
  if (!isLookSystem(theme)) return "";
  const colorSystem = theme.colorSystem;
  const light = schemeColours(theme, "light");
  const dark = schemeColours(theme, "dark");
  const palette = paletteVars(colorSystem, "light");
  const page = light.surfaces.page;
  const pageVars = page ? lookVars(page, light, true) : {};
  const pageGlobals = globalVars(light, pageVars);
  const darkPage = dark.surfaces.page;
  const darkPageVars = darkPage ? lookVars(darkPage, dark, true) : {};
  return [
    rule(
      SCOPE,
      {
        ...palette,
        ...redirectVars(colorSystem.redirects, palette),
        ...roleVars(colorSystem, palette),
        ...pageVars,
        ...pageGlobals,
        ...namedGlobalVars(light, pageVars),
      },
      page,
    ),
    SECTION_TEXT_RULE,
    ...backgroundRules(SCOPE, light),
    darkPage
      ? rule(
          DARK_SCOPE,
          {
            ...paletteVars(colorSystem, "dark"),
            ...ownColourDarkVars(colorSystem),
            ...darkPageVars,
            ...changedFrom(globalVars(dark, darkPageVars), pageGlobals),
            ...namedGlobalVars(dark, darkPageVars),
          },
          darkPage,
        )
      : "",
    ...backgroundRules(DARK_SCOPE, dark),
    OWN_DARK_RULE,
    rule(
      colorSystem.base === "dark" ? DARK_SCOPE : SCOPE,
      legacyVars(colorSystem.legacy),
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

/**
 * One scheme's colours on :root, for a page captured before hydration copies data-re-colors and data-re-scheme to
 * <html> (the guide's thumbnails). Light rules come first so the dark ones win by order, as they win by specificity
 * once hydrated; from then on the html[data-re-colors] rules outrank these.
 */
export function colorSystemFirstPaintCss(
  theme: unknown,
  scheme: LookScheme,
): string {
  const rules = colorSystemCss(theme).split("\n").filter(Boolean);
  const dark = (rule: string) => rule.startsWith(DARK_SCOPE);
  return [
    ...rules.filter((rule) => !dark(rule)),
    ...(scheme === "dark" ? rules.filter(dark) : []),
  ]
    .map(
      (rule) => `:root${rule.slice((dark(rule) ? DARK_SCOPE : SCOPE).length)}`,
    )
    .join("\n");
}
